/**
 * Navigation Queue Service
 *
 * FIFO queue for navigation actions that should be deferred until modals close.
 * Prevents navigation conflicts when modals are open (e.g., clicking notification
 * while a modal is visible).
 *
 * Example:
 * - User clicks notification → queueNavigation() → closeModal()
 * - When modal closes → flush() returns queued actions → Dashboard executes
 */

import { NavigationAction } from '../types/modal-manager.types';

class NavigationQueueService {
  private queue: NavigationAction[] = [];

  /**
   * Add a navigation action to the queue
   * @param action - The navigation action to queue
   */
  public enqueue(action: NavigationAction): void {
    this.queue.push(action);
  }

  /**
   * Get all queued actions and clear the queue (FIFO)
   * @returns Array of queued navigation actions
   */
  public flush(): NavigationAction[] {
    const actions = [...this.queue];
    this.queue = [];
    return actions;
  }

  /**
   * Check if queue has pending actions
   * @returns true if queue is not empty
   */
  public hasPending(): boolean {
    return this.queue.length > 0;
  }

  /**
   * Get the number of pending actions
   * @returns Number of actions in queue
   */
  public getCount(): number {
    return this.queue.length;
  }

  /**
   * Clear all queued actions without executing them
   */
  public clear(): void {
    this.queue = [];
  }

  /**
   * Peek at the next action without removing it
   * @returns The next action or undefined if queue is empty
   */
  public peek(): NavigationAction | undefined {
    return this.queue[0];
  }
}

// Singleton instance
export const navigationQueueService = new NavigationQueueService();
